import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useChatContext } from '../../context/ChatContext';

const { FiPlus, FiMessageSquare, FiTrash2, FiEdit2, FiCheck, FiX, FiChevronLeft } = FiIcons;

/**
 * Group conversations by relative date
 */
function groupConversationsByDate(conversations) {
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    thisMonth: [],
    older: [],
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  for (const conv of conversations) {
    const convDate = new Date(conv.updatedAt || conv.createdAt);
    const convDay = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

    if (convDay >= today) {
      groups.today.push(conv);
    } else if (convDay >= yesterday) {
      groups.yesterday.push(conv);
    } else if (convDay >= weekAgo) {
      groups.thisWeek.push(conv);
    } else if (convDay >= monthAgo) {
      groups.thisMonth.push(conv);
    } else {
      groups.older.push(conv);
    }
  }

  return groups;
}

const ConversationItem = ({ conversation, isActive, onSelect, onDelete, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);
  const [showActions, setShowActions] = useState(false);

  const handleRename = async () => {
    if (editTitle.trim() && editTitle !== conversation.title) {
      await onRename(conversation.id, editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditTitle(conversation.title);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isActive
          ? 'bg-primary-100 text-primary-900'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
      onClick={() => !isEditing && onSelect(conversation.id)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <SafeIcon
        icon={FiMessageSquare}
        className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-400'}`}
      />

      {isEditing ? (
        <div className="flex-1 flex items-center gap-1">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRename();
            }}
            className="p-1 text-green-600 hover:text-green-700"
          >
            <SafeIcon icon={FiCheck} className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditTitle(conversation.title);
              setIsEditing(false);
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          <span className="flex-1 truncate text-sm">{conversation.title}</span>

          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Rename"
                >
                  <SafeIcon icon={FiEdit2} className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conversation.id);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 rounded"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

const ConversationGroup = ({ title, conversations, activeId, onSelect, onDelete, onRename }) => {
  if (conversations.length === 0) return null;

  return (
    <div className="mb-4">
      <h4 className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
        {title}
      </h4>
      <div className="space-y-1">
        {conversations.map((conv) => (
          <ConversationItem
            key={conv.id}
            conversation={conv}
            isActive={conv.id === activeId}
            onSelect={onSelect}
            onDelete={onDelete}
            onRename={onRename}
          />
        ))}
      </div>
    </div>
  );
};

const ConversationSidebar = ({ onClose }) => {
  const {
    conversations,
    activeConversationId,
    loadConversation,
    startNewConversation,
    deleteConversation,
    renameConversation,
    chatLoading,
  } = useChatContext();

  const groupedConversations = groupConversationsByDate(conversations);

  const handleNewConversation = () => {
    startNewConversation();
  };

  const handleSelectConversation = async (conversationId) => {
    await loadConversation(conversationId);
  };

  const handleDeleteConversation = async (conversationId) => {
    if (window.confirm('Delete this conversation? This cannot be undone.')) {
      await deleteConversation(conversationId);
    }
  };

  const handleRenameConversation = async (conversationId, newTitle) => {
    await renameConversation(conversationId, newTitle);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">Conversations</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          title="Close sidebar"
        >
          <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
        </button>
      </div>

      {/* New Conversation Button */}
      <div className="p-3">
        <button
          onClick={handleNewConversation}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          New Conversation
        </button>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {chatLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <SafeIcon icon={FiMessageSquare} className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No conversations yet</p>
            <p className="text-xs mt-1">Start a new conversation above</p>
          </div>
        ) : (
          <>
            <ConversationGroup
              title="Today"
              conversations={groupedConversations.today}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
            <ConversationGroup
              title="Yesterday"
              conversations={groupedConversations.yesterday}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
            <ConversationGroup
              title="This Week"
              conversations={groupedConversations.thisWeek}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
            <ConversationGroup
              title="This Month"
              conversations={groupedConversations.thisMonth}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
            <ConversationGroup
              title="Older"
              conversations={groupedConversations.older}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationSidebar;
